// ============================================================
// DEALIN MVP - Protected Route Component
// ============================================================

import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { PageLoading } from '@/components/ui/Spinner';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireVendor?: boolean;
}

export const ProtectedRoute = ({ children, requireVendor = false }: ProtectedRouteProps) => {
  const { isAuthenticated, isInitialized, isVendor } = useAuth();
  const location = useLocation();

  // Show loading while initializing
  if (!isInitialized) {
    return <PageLoading />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check vendor requirement
  if (requireVendor && !isVendor) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

/**
 * Redirect authenticated users away from auth pages
 */
export const PublicOnlyRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isInitialized, isVendor, isClient } = useAuth();
  const location = useLocation();

  if (!isInitialized) {
    return <PageLoading />;
  }

  if (isAuthenticated) {
    // Redirect to the page they came from
    const from = (location.state as { from?: { pathname: string } })?.from?.pathname;
    if (from && from !== '/login' && from !== '/register') {
      return <Navigate to={from} replace />;
    }
    
    // Redirect based on user type
    if (isVendor) {
      return <Navigate to="/manage?page=dashboard" replace />;
    } else if (isClient) {
      return <Navigate to="/home" replace />;
    }
    
    // Fallback to home
    return <Navigate to="/home" replace />;
  }

  return <>{children}</>;
};
